// JLR Master Engine List
//
// Per-model engine specs (HP, torque, 0-60, top speed) used by the VIN lookup section and
// any other page that needs to render JLR engine data. Exposed as
// window.JLREngines.
//
// Engine code format: letter prefix (P=petrol, D=diesel) + 3-digit PS number +
// optional `e` suffix for hybrids (e.g. P400, D350, P550e).
//
// API:
//   window.JLREngines.registerSpecs(rawModel, specs)
//     Adds or replaces model-specific specs from Shopify torque_table entries
//     with kind="specifications". Called by snippets/oem-engine-specs.liquid.
//
//   window.JLREngines.data
//     Raw master array: [{ model, engines: [{ engine, hp, ps?, torque?, zeroSixty?, topSpeed? }] }]
//     `ps` is only needed when the manufacturer's published output differs from
//     the nominal number in the engine code (for example, D200 = 204 PS).
//     Omit unconfirmed model-specific torque, acceleration and top-speed figures.
//
//   window.JLREngines.normalizeModel(rawModel)
//     Maps a VIN-API model string to a canonical entry in `data`. Returns null
//     if the model isn't in our list. Notes:
//       - Defender 90 / 110 / 130 are not reliably distinguishable from the VIN,
//         so every Defender (other than OCTA) normalizes to "Defender 110".
//       - Range Rover LWB falls back to "Range Rover (SWB)" — same engines.
//
//   window.JLREngines.enginesFor(rawModel)
//     Returns the engines array for the model, or [].
//
//   window.JLREngines.lookupEngine(rawModel, code, tolerance)
//     Returns the engine spec for `code` within the model's engines.
//       tolerance = 0 (default): exact code match. Use for merchant-controlled
//         target codes parsed from activation titles.
//       tolerance > 0: accept any same-prefix, same-suffix engine within
//         ±tolerance PS, preferring nearest. Use for the customer's CURRENT
//         engine since the VIN API sometimes reports PS values that drift a few
//         units from the nominal (e.g. a D250 vehicle reports "249PS").

(function () {
  "use strict";

  var MASTER = [
    {
      model: "Jaguar F-PACE",
      engines: [
        { engine: "P250",  hp: 246, torque: 269, zeroSixty: 6.9 },
        { engine: "P340",  hp: 335, torque: 354, zeroSixty: 5.8 },
        { engine: "P400",  hp: 395, torque: 406, zeroSixty: 5.1 },
        { engine: "P400e", hp: 398, torque: 472, zeroSixty: 5.0 },
        { engine: "P550",  hp: 542, torque: 516, zeroSixty: 3.8 },
        { engine: "D165",  hp: 161, torque: 280, zeroSixty: 9.2 },
        { engine: "D200",  hp: 197, torque: 317, zeroSixty: 7.6 },
        { engine: "D300",  hp: 296, torque: 479, zeroSixty: 6.1 }
      ]
    },
    {
      model: "Range Rover Velar",
      engines: [
        { engine: "P250",  hp: 246, torque: 269, zeroSixty: 7.1 },
        { engine: "P340",  hp: 335, torque: 354, zeroSixty: 6.0 },
        { engine: "P400",  hp: 395, torque: 406, zeroSixty: 5.2 },
        { engine: "P400e", hp: 398, torque: 472, zeroSixty: 5.1 },
        { engine: "D200",  hp: 197, torque: 317, zeroSixty: 7.7 },
        { engine: "D300",  hp: 296, torque: 479, zeroSixty: 6.1 }
      ]
    },
    {
      model: "Range Rover Sport",
      engines: [
        { engine: "P360",  hp: 355, torque: 369, zeroSixty: 5.7 },
        { engine: "P400",  hp: 395, torque: 406, zeroSixty: 5.2 },
        { engine: "P440e", hp: 434, torque: 457, zeroSixty: 5.5 },
        { engine: "P460e", hp: 454, torque: 487, zeroSixty: 5.3 },
        { engine: "P510e", hp: 503, torque: 516, zeroSixty: 5.2 },
        { engine: "P530",  hp: 523, torque: 553, zeroSixty: 4.3 },
        { engine: "P550e", hp: 543, torque: 590, zeroSixty: 4.7 },
        { engine: "P635",  hp: 626, torque: 553, zeroSixty: 3.7 },
        { engine: "D250",  hp: 246, torque: 443, zeroSixty: 7.5 },
        { engine: "D300",  hp: 296, torque: 479, zeroSixty: 6.3 },
        { engine: "D350",  hp: 345, torque: 516, zeroSixty: 5.6 }
      ]
    },
    {
      model: "Range Rover (SWB)",
      engines: [
        { engine: "P360",  hp: 355, torque: 369, zeroSixty: 6.0 },
        { engine: "P400",  hp: 395, torque: 406, zeroSixty: 5.5 },
        { engine: "P460e", hp: 454, torque: 487, zeroSixty: 5.5 },
        { engine: "P530",  hp: 523, torque: 553, zeroSixty: 4.4 },
        { engine: "P550e", hp: 543, torque: 590, zeroSixty: 4.8 },
        { engine: "P615",  hp: 606, torque: 553, zeroSixty: 4.3 },
        { engine: "P635",  hp: 626, torque: 553, zeroSixty: 3.8 },
        { engine: "D250",  hp: 246, torque: 443, zeroSixty: 7.8 },
        { engine: "D300",  hp: 296, torque: 479, zeroSixty: 6.5 },
        { engine: "D350",  hp: 345, torque: 516, zeroSixty: 5.8 }
      ]
    },
    {
      model: "Land Rover Discovery Sport",
      engines: [
        { engine: "D165", hp: 161, ps: 163, torque: 280, zeroSixty: 9.5, topSpeed: 121 },
        { engine: "D200", hp: 201, ps: 204, torque: 317, zeroSixty: 8.1, topSpeed: 131 },
        { engine: "P200", hp: 197, ps: 200, torque: 236, zeroSixty: 8.9, topSpeed: 129 },
        { engine: "P250", hp: 247, ps: 250, torque: 269, zeroSixty: 8.1, topSpeed: 129 }
      ]
    },
    {
      model: "Land Rover Discovery",
      engines: [
        { engine: "P300",  hp: 296, torque: 347, zeroSixty: 6.9 },
        { engine: "P360",  hp: 355, torque: 369, zeroSixty: 6.2 },
        { engine: "D250",  hp: 246, torque: 443, zeroSixty: 7.6 },
        { engine: "D300",  hp: 296, torque: 479, zeroSixty: 6.5 }
      ]
    },
    {
      model: "Defender 90",
      engines: [
        { engine: "P300",  hp: 296, torque: 347, zeroSixty: 6.7 },
        { engine: "P400",  hp: 395, torque: 406, zeroSixty: 5.7 },
        { engine: "P425",  hp: 419, torque: 406, zeroSixty: 5.3 },
        { engine: "P525",  hp: 518, torque: 461, zeroSixty: 4.9 },
        { engine: "D200",  hp: 197, torque: 369, zeroSixty: 9.1 },
        { engine: "D250",  hp: 246, torque: 443, zeroSixty: 7.6 },
        { engine: "D350",  hp: 345, torque: 516, zeroSixty: 5.9 }
      ]
    },
    {
      model: "Defender 110",
      engines: [
        { engine: "P300",  hp: 296, torque: 347, zeroSixty: 7.0 },
        { engine: "P300e", hp: 296, torque: 461, zeroSixty: 7.4 },
        { engine: "P400",  hp: 395, torque: 406, zeroSixty: 5.8 },
        { engine: "P400e", hp: 398, torque: 472, zeroSixty: 5.4 },
        { engine: "P425",  hp: 419, torque: 406, zeroSixty: 5.5 },
        { engine: "P500",  hp: 493, torque: 450, zeroSixty: 5.2 },
        { engine: "P525",  hp: 518, torque: 461, zeroSixty: 5.1 },
        { engine: "P635",  hp: 626, torque: 553, zeroSixty: 3.8 },
        { engine: "D200",  hp: 197, torque: 369, zeroSixty: 9.5 },
        { engine: "D250",  hp: 246, torque: 443, zeroSixty: 7.9 },
        { engine: "D300",  hp: 296, torque: 479, zeroSixty: 6.6 },
        { engine: "D350",  hp: 345, torque: 516, zeroSixty: 6.1 }
      ]
    },
    {
      model: "Defender 130",
      engines: [
        { engine: "P300",  hp: 296, torque: 347, zeroSixty: 7.5 },
        { engine: "P400",  hp: 395, torque: 406, zeroSixty: 6.3 },
        { engine: "P425",  hp: 419, torque: 406, zeroSixty: 5.8 },
        { engine: "P500",  hp: 493, torque: 450, zeroSixty: 5.4 },
        { engine: "D250",  hp: 246, torque: 443, zeroSixty: 8.4 },
        { engine: "D300",  hp: 296, torque: 479, zeroSixty: 7.1 },
        { engine: "D350",  hp: 345, torque: 516, zeroSixty: 6.4 }
      ]
    },
    {
      model: "Defender OCTA",
      engines: [
        { engine: "P635",  hp: 626, torque: 553, zeroSixty: 3.6 }
      ]
    }
  ];

  function normalizeModel(rawModel) {
    if (!rawModel) return null;
    var m = String(rawModel).toLowerCase();
    if (m.indexOf("octa") !== -1) return "Defender OCTA";
    if (m.indexOf("defender") !== -1) return "Defender 110";
    if (m.indexOf("range rover sport") !== -1) return "Range Rover Sport";
    if (m.indexOf("range rover velar") !== -1) return "Range Rover Velar";
    if (m.indexOf("range rover") !== -1) return "Range Rover (SWB)";
    if (m.indexOf("f-pace") !== -1 || m.indexOf("f pace") !== -1) return "Jaguar F-PACE";
    if (m.indexOf("discovery sport") !== -1) return "Land Rover Discovery Sport";
    if (m.indexOf("discovery") !== -1) return "Land Rover Discovery";
    return null;
  }

  function enginesFor(rawModel) {
    var canonical = normalizeModel(rawModel);
    if (!canonical) return [];
    for (var i = 0; i < MASTER.length; i++) {
      if (MASTER[i].model === canonical) return MASTER[i].engines;
    }
    return [];
  }

  function lookupEngine(rawModel, code, tolerance) {
    if (!code) return null;
    var engines = enginesFor(rawModel);
    if (!engines.length) return null;

    var i;
    if (!tolerance) {
      for (i = 0; i < engines.length; i++) {
        if (engines[i].engine === code) return engines[i];
      }
      return null;
    }

    var parts = code.match(/^([PDE])(\d+)(e?)$/i);
    if (!parts) return null;
    var prefix = parts[1].toUpperCase();
    var psValue = parseInt(parts[2], 10);
    var hasE = !!parts[3];

    var best = null;
    var bestDist = Infinity;
    for (i = 0; i < engines.length; i++) {
      var k = engines[i].engine;
      var mm = k.match(/^([PDE])(\d+)(e?)$/i);
      if (!mm) continue;
      if (mm[1].toUpperCase() !== prefix) continue;
      if (!!mm[3] !== hasE) continue;
      var dist = Math.abs(parseInt(mm[2], 10) - psValue);
      if (dist <= tolerance && dist < bestDist) {
        bestDist = dist;
        best = engines[i];
      }
    }
    return best;
  }

  // Merchant-authored specs from Shopify take precedence over the legacy list.
  function registerSpecs(rawModel, specs) {
    if (!Array.isArray(specs) || !normalizeModel(rawModel)) return;
    var engines = enginesFor(rawModel);
    specs.forEach(function (spec) {
      if (!spec || !/^[PD]\d{3}e?$/.test(spec.engine) || typeof spec.hp !== 'number' || !isFinite(spec.hp) || spec.hp <= 0) return;
      for (var i = 0; i < engines.length; i++) {
        if (engines[i].engine === spec.engine) {
          engines[i] = spec;
          return;
        }
      }
      engines.push(spec);
    });
  }

  window.JLREngines = {
    data: MASTER,
    normalizeModel: normalizeModel,
    enginesFor: enginesFor,
    lookupEngine: lookupEngine,
    registerSpecs: registerSpecs
  };
})();
